import { createClient } from '@supabase/supabase-js';

/**
 * The browser owns the auth session. supabase-js persists it to localStorage
 * and refreshes the access token in the background before it expires, which is
 * what keeps users signed in indefinitely without the server holding any state.
 * Every API call sends the current access token as a bearer token; the backend
 * only verifies its signature.
 */
/**
 * Whether this page load came from a password recovery link.
 *
 * Captured synchronously at import time because detectSessionInUrl strips the
 * hash asynchronously right after — by the time a component mounts, the marker
 * is gone. Reading it here is deterministic; listening for PASSWORD_RECOVERY in
 * a useEffect races the client's own URL handling.
 */
export const isPasswordRecoveryLink =
  new URLSearchParams(window.location.hash.substring(1)).get('type') === 'recovery';

export const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL!,
  process.env.REACT_APP_SUPABASE_ANON_KEY!,
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      // Consumes the tokens Supabase appends to OAuth / recovery redirects, so
      // no page needs to parse window.location.hash itself. Left on the default
      // implicit flow: PKCE would switch email links to a ?code= exchange and
      // drop the type=recovery marker the reset page keys off.
      detectSessionInUrl: true,
    },
  }
);

/**
 * Current access token, refreshed first if it is at or near expiry.
 * Returns null when signed out.
 */
export const getAccessToken = async (): Promise<string | null> => {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
};

export default supabase;
